/**
 * Helpers for converting between the GraphQL HTML template fragment and the
 * input shape the create/update mutations expect. Apollo decorates fragments
 * with `__typename` everywhere, which has to be stripped before the result can
 * be re-submitted as input.
 */

import type {
  HtmlDocumentTemplateFieldsFragment,
  HtmlDocumentTemplateStyleInput
} from '~/api/graphql';

export type HtmlTemplateStyleFragment = NonNullable<HtmlDocumentTemplateFieldsFragment['style']>;

export function omitGraphQLTypenames(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(omitGraphQLTypenames);
  }
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k === '__typename') {
      continue;
    }
    out[k] = typeof v === 'object' && v !== null ? omitGraphQLTypenames(v) : v;
  }
  return out;
}

export function templateStyleToFormData(
  style: HtmlTemplateStyleFragment
): HtmlDocumentTemplateStyleInput {
  return omitGraphQLTypenames(style) as HtmlDocumentTemplateStyleInput;
}

/**
 * Build the editor's initial style state from a loaded template fragment.
 * Returns null when the template has no saved style overrides yet so callers
 * can distinguish "nothing set" from "every field explicitly empty".
 */
export function initialTemplateStyleFromFragment(
  template?: HtmlDocumentTemplateFieldsFragment
): HtmlDocumentTemplateStyleInput | null {
  if (!template?.style) return null;
  return templateStyleToFormData(template.style);
}
